import React from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { NotificationContainer } from '@web/walter-ui-kit'
import Gradients from '@web/walter-ui-kit/components/Chart/Gradients'
import { enableVhPlugin } from '@web/walter-lib/vh-plugin'
import Main from './Main'
import SocketConnection from './SocketConnection'
import AuthLayout from '~components/App/AuthLayout'
import { SearchSlotProvider } from '~contexts/SearchContext'
import MarketPage from '~pages/MarketPage'
import BuyPage from '~pages/BuyPage'
import SwapPage from '~pages/SwapPage'
import WalletPage from '~pages/WalletPage'
import StakingPage from '~pages/StakingPage'
import HistoryPage from '~pages/HistoryPage'
import CardsPage from '~pages/CardsPage'
import AgreementPage from '~pages/AgreementPage'

import { Routes as AppRoutes } from '~constants/routes'
import AuthContextLayer from '~contexts/AuthContext'
import { ModalsContextLayer } from '~contexts/ModalsContext'
import UserSettingsLayer from '~contexts/UserSettingsContext'
import { BaseCurrencyProvider } from '~contexts/BaseСurrencyContext'
import ScrollToTop from '~components/ScrollToTop'
import { useCommonCMSConfig } from '~api'
import { isClient } from '~utils/isClient'
import SendPage from '~pages/SendPage'
import ReceivePage from '~pages/ReceivePage'
import AssetPage from '~pages/AssetPage'
import TransferPage from '~pages/TransferPage'
import GlobalModalsContainer from '~modals/GlobalModalsContainer'
import { RequireAuth } from '~components/RouteGuard/RequireAuth/RequireAuth'
import { SkipAuth } from '~components/RouteGuard/SkipAuth/SkipAuth'

const SignUpPage = React.lazy(() => import('~pages/AuthorizationPages/SignUp'))
const SignInPage = React.lazy(() => import('~pages/AuthorizationPages/SignIn'))
const CountrySelectRegisterPage = React.lazy(() => import('~pages/AuthorizationPages/CountrySelectRegister'))
const SignInCodeVerificationPage = React.lazy(() => import('~pages/AuthorizationPages/SignInCodeVerification'))
const SignUpCodeVerificationPage = React.lazy(() => import('~pages/AuthorizationPages/SignUpCodeVerification'))
const SignUpPhoneVerificationPage = React.lazy(() => import('~pages/AuthorizationPages/SignUpPhoneVerification'))
const PasswordRecoveryPage = React.lazy(() => import('~pages/AuthorizationPages/PasswordRecovery'))
const PasswordRecoveryCreationPage = React.lazy(() => import('~pages/AuthorizationPages/PasswordRecoveryCreation'))
const PasswordRecoverySuccessPage = React.lazy(() => import('~pages/AuthorizationPages/PasswordRecoverySuccess'))
const ServiceNotAvailablePage = React.lazy(() => import('~pages/AuthorizationPages/ServiceNotAvailable'))
const SignInBlockedPage = React.lazy(() => import('~pages/AuthorizationPages/SignInBlocked'))
const SignInTwoFaPage = React.lazy(() => import('~pages/AuthorizationPages/SignInTwoFa'))
const SignUpSuccessPage = React.lazy(() => import('~pages/AuthorizationPages/SignUpSuccess'))
const SocialAuth = React.lazy(() => import('~pages/AuthorizationPages/SocialAuth'))
const InviteSocialAuthPage = React.lazy(() => import('~pages/AuthorizationPages/InviteSocialAuth'))

if (isClient()) {
    enableVhPlugin()
}

/**
 * Providers component
 */
// eslint-disable-next-line complexity, max-lines-per-function
export function Providers() {
    const { data, isValidating: isConfigLoading } = useCommonCMSConfig()
    const {
        showSwapPage,
        showStaking,
        showReferral,
        isBuyDisabled,
        hideMarketPage,
        showTransfer,
        mobileRegistrationEnabled,
        signUpInviteOnly,
    } = data?.features || {}

    if (isConfigLoading) {
        return null
    }

    return (
        <UserSettingsLayer>
            <NotificationContainer>
                <AuthContextLayer>
                    <SocketConnection>
                        <BaseCurrencyProvider>
                            <SearchSlotProvider>
                                <ModalsContextLayer>
                                    <Gradients />
                                    <ScrollToTop />
                                    <GlobalModalsContainer showReferral={showReferral} showStaking={showStaking} />
                                    <Routes>
                                        <Route path="/" element={<AuthLayout />}>
                                            <Route element={<SkipAuth />}>
                                                <Route path={AppRoutes.signUp} element={<SignUpPage />} />
                                                <Route path={AppRoutes.signIn} element={<SignInPage />} />
                                                <Route
                                                    path={AppRoutes.chooseCountry}
                                                    element={<CountrySelectRegisterPage />}
                                                />
                                                <Route
                                                    path={AppRoutes.verifyEmailSignIn}
                                                    element={<SignInCodeVerificationPage />}
                                                />
                                                {mobileRegistrationEnabled && (
                                                    <Route
                                                        path={AppRoutes.verifyPhoneSignUp}
                                                        element={<SignUpPhoneVerificationPage />}
                                                    />
                                                )}
                                                <Route
                                                    path={AppRoutes.passwordRecovery}
                                                    element={<PasswordRecoveryPage />}
                                                />
                                                <Route
                                                    path={AppRoutes.passwordRecoveryCreation}
                                                    element={<PasswordRecoveryCreationPage />}
                                                />
                                                <Route
                                                    path={AppRoutes.passwordRecoverySuccess}
                                                    element={<PasswordRecoverySuccessPage />}
                                                />
                                                <Route path={AppRoutes.signInBlocked} element={<SignInBlockedPage />} />
                                                <Route
                                                    path={AppRoutes.serviceNotAvailable}
                                                    element={<ServiceNotAvailablePage />}
                                                />
                                            </Route>
                                            <Route
                                                path={`${AppRoutes.cards}/:cardId/agreements/:agreementId`}
                                                element={<AgreementPage />}
                                            />
                                            <Route path={AppRoutes.twoFaCode} element={<SignInTwoFaPage />} />
                                            <Route path={AppRoutes.signUpSuccess} element={<SignUpSuccessPage />} />
                                            <Route
                                                path={AppRoutes.verifyEmailSignUp}
                                                element={<SignUpCodeVerificationPage />}
                                            />
                                            <Route path={AppRoutes.socialAuth} element={<SocialAuth />} />
                                            {signUpInviteOnly && (
                                                <Route
                                                    path={AppRoutes.inviteSocialAuth}
                                                    element={<InviteSocialAuthPage />}
                                                />
                                            )}
                                        </Route>
                                        <Route element={<RequireAuth />}>
                                            <Route path="/" element={<Main />}>
                                                {!hideMarketPage && (
                                                    <Route path={AppRoutes.market} element={<MarketPage />} />
                                                )}
                                                <Route path={AppRoutes.wallet} element={<WalletPage />} />
                                                <Route path={AppRoutes.cards} element={<CardsPage />} />
                                                <Route path={`${AppRoutes.asset}/:code`} element={<AssetPage />} />
                                                <Route path={AppRoutes.send} element={<SendPage />} />
                                                <Route path={AppRoutes.receive} element={<ReceivePage />} />
                                                {showTransfer && (
                                                    <Route path={AppRoutes.transfer} element={<TransferPage />} />
                                                )}
                                                {(showSwapPage || isConfigLoading) && (
                                                    <Route path={AppRoutes.swap} element={<SwapPage />} />
                                                )}
                                                {(!isBuyDisabled || isConfigLoading) && (
                                                    <Route path={AppRoutes.buy} element={<BuyPage />} />
                                                )}
                                                {(showStaking || isConfigLoading) && (
                                                    <Route path={AppRoutes.staking} element={<StakingPage />} />
                                                )}
                                                <Route path={AppRoutes.history} element={<HistoryPage />} />
                                                <Route index element={<Navigate to={AppRoutes.wallet} />} />
                                            </Route>
                                        </Route>
                                        <Route path="*" element={<Navigate to={AppRoutes.wallet} />} />
                                    </Routes>
                                </ModalsContextLayer>
                            </SearchSlotProvider>
                        </BaseCurrencyProvider>
                    </SocketConnection>
                </AuthContextLayer>
            </NotificationContainer>
        </UserSettingsLayer>
    )
}
